import { useState, useEffect } from "react";
import { ChevronDown, MapPin } from "lucide-react";

const EVENT_DATE = new Date("2026-04-27T04:30:00.000Z"); // 10:00 AM IST

function Countdown() {
  const [time, setTime] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const diff = EVENT_DATE - now;
      if (diff <= 0) { setTime({ days: 0, hours: 0, minutes: 0, seconds: 0 }); return; }
      setTime({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const units = [
    { label: "Days", value: time.days },
    { label: "Hours", value: time.hours },
    { label: "Minutes", value: time.minutes },
    { label: "Seconds", value: time.seconds },
  ];

  return (
    <div data-testid="countdown-timer" className="flex items-center gap-3 sm:gap-5">
      {units.map(({ label, value }, i) => (
        <div key={label} className="flex items-center gap-3 sm:gap-5">
          <div className="countdown-box flex flex-col items-center justify-center w-16 h-16 sm:w-20 sm:h-20">
            <span data-testid={`countdown-${label.toLowerCase()}`} className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl font-semibold text-[#D4AF37] leading-none">
              {String(value).padStart(2, "0")}
            </span>
            <span className="text-[0.55rem] tracking-[0.18em] uppercase text-[#94A3B8] mt-1">{label}</span>
          </div>
          {i < 3 && <span className="text-[#D4AF37]/50 text-xl font-light">:</span>}
        </div>
      ))}
    </div>
  );
}

export default function HeroSection() {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="hero-pattern relative min-h-screen flex flex-col items-center justify-center text-center px-6 pt-20 pb-16 overflow-hidden"
    >
      {/* Emerald glow orb */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(6,78,59,0.35) 0%, transparent 70%)" }} />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(212,175,55,0.08) 0%, transparent 70%)" }} />

      <div className="relative z-10 max-w-4xl mx-auto flex flex-col items-center gap-6 animate-fadeInUp">
        {/* Flags */}
        <div data-testid="flags-strip" className="flex items-center gap-4 mb-2">
          {[
            { src: "https://flagcdn.com/w40/eu.png", alt: "European Union", delay: "0s", label: "EU" },
            { src: "https://flagcdn.com/w40/cy.png", alt: "Cyprus", delay: "0.5s", label: "CY" },
            { src: "https://flagcdn.com/w40/in.png", alt: "India", delay: "1s", label: "IN" },
          ].map(({ src, alt, delay, label }, i) => (
            <div key={label} className="flex items-center gap-4">
              {i > 0 && <div className="w-px h-6 bg-[#D4AF37]/30" />}
              <img
                src={src}
                alt={alt}
                data-testid={`flag-${label.toLowerCase()}`}
                className="flag-img animate-float"
                style={{ animationDelay: delay }}
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling && (e.target.nextSibling.style.display = "flex");
                }}
              />
              <span style={{ display: "none" }} className="text-[#D4AF37] text-xs font-bold border border-[#D4AF37]/30 px-2 py-1">{label}</span>
            </div>
          ))}
        </div>

        {/* Overline */}
        <div data-testid="invite-only-badge" className="flex items-center gap-3">
          <div className="w-8 h-px bg-[#D4AF37]/60" />
          <span className="overline-gold text-[0.68rem]">By Invitation Only &nbsp;·&nbsp; 50 Seats</span>
          <div className="w-8 h-px bg-[#D4AF37]/60" />
        </div>

        {/* Main Title */}
        <h1 data-testid="hero-title" className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl lg:text-6xl font-semibold text-white leading-tight tracking-tight">
          Strengthening EU–India<br />
          <span className="gold-shimmer">Strategic &amp; Economic</span><br />
          Partnerships
        </h1>

        {/* Event type */}
        <p data-testid="hero-subtitle" className="overline-gold text-[0.75rem] tracking-[0.25em]">
          Special Policy &amp; Business Dialogue
        </p>

        {/* Gold divider */}
        <div className="gold-divider w-64" />

        {/* Event meta */}
        <div data-testid="hero-event-meta" className="flex flex-wrap justify-center items-center gap-4 text-[#94A3B8] text-sm font-['Outfit']">
          <span className="flex items-center gap-1.5">
            <span className="w-1 h-1 bg-[#D4AF37] rounded-full inline-block" />
            Monday, 27 April 2026
          </span>
          <span className="text-[#D4AF37]/30">|</span>
          <span>10:00 AM – 1:00 PM IST</span>
          <span className="text-[#D4AF37]/30">|</span>
          <span className="flex items-center gap-1">
            <MapPin size={13} className="text-[#D4AF37]" />
            DoubleTree by Hilton, Ahmedabad
          </span>
        </div>

        {/* Countdown */}
        <div className="py-4">
          <p className="overline-gold text-[0.6rem] mb-4 text-center">Commences In</p>
          <Countdown />
        </div>

        {/* CTAs */}
        <div className="flex flex-wrap justify-center gap-4 mt-2">
          <button
            data-testid="hero-cta-primary"
            onClick={() => document.getElementById("register")?.scrollIntoView({ behavior: "smooth" })}
            className="btn-gold"
          >
            Request Your Seat
            <ChevronDown size={15} />
          </button>
          <button
            data-testid="hero-cta-secondary"
            onClick={() => document.getElementById("agenda")?.scrollIntoView({ behavior: "smooth" })}
            className="btn-outline-gold"
          >
            View Programme
          </button>
        </div>

        {/* Hosted by */}
        <p className="text-[#94A3B8] text-xs font-['Outfit'] mt-2">
          Presented by <span className="text-[#D4AF37]">Digpu News &amp; Media</span> &nbsp;·&nbsp; Organised by <span className="text-[#D4AF37]">Water &amp; Shark</span> &amp; <span className="text-[#D4AF37]">EICBI</span>
        </p>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-40">
        <span className="overline-gold text-[0.55rem]">Scroll</span>
        <ChevronDown size={16} className="text-[#D4AF37] animate-bounce" />
      </div>
    </section>
  );
}
